import { Injectable } from '@angular/core';
import { HttpRequestState } from 'ngx-http-request-state';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { getLogger, setFaroSessionAttributes } from 'tofe-core';
import { CopTOFEConfiguration, TaskInfo } from 'top-api-sdk-angular';

import { SessionInfo } from './types';

const debug = getLogger('[Service][TopSession]').debug;

export type SessionInfoRequestState = HttpRequestState<SessionInfo>;

@Injectable()
export class TopSessionService {
  readonly session$: Observable<SessionInfoRequestState>;
  private readonly sessionSubject = new BehaviorSubject<Observable<SessionInfoRequestState>>(
    of({ isLoading: true, error: undefined }),
  );

  private sessionSnapshot?: Readonly<SessionInfo>;

  constructor() {
    this.session$ = this.sessionSubject.asObservable().pipe(
      switchMap((x) => x),
      tap((session) => {
        this.sessionSnapshot = Object.freeze(session.value);
        debug('session$', session);

        if (session.value?.processData) {
          const { id, processData } = session.value;
          const { companyId, dossierId, dossierType } = processData;
          setFaroSessionAttributes({
            tofeSessionId: id ?? '',
            companyId: companyId ?? '',
            dossierId: dossierId ?? '',
            dossierType: dossierType ?? '',
          });
        }
      }),
      shareReplay(1),
    );
  }

  setSession(session$: Observable<SessionInfoRequestState>) {
    this.sessionSubject.next(session$);
  }

  hasSession(): boolean {
    return !!this.sessionSnapshot;
  }

  getSession(): Readonly<SessionInfo> {
    if (!this.sessionSnapshot) {
      return {} as SessionInfo;
    }
    return this.sessionSnapshot;
  }

  getTofeConfiguration(): Readonly<CopTOFEConfiguration> {
    // `ProcessInfo.tofeConfiguration` is typed as the base `ICopTOFEConfiguration`
    // (just `{ type: string }`) in the current spec; `CopTOFEConfiguration` is the
    // concrete shape with the actual customization fields.
    const ret = (this.getSession()?.processData?.tofeConfiguration ?? {}) as CopTOFEConfiguration;
    return Object.freeze(ret);
  }

  /**
   * This method is an easier way to get taskId from session
   * we prefer it instead of using session$ to reduce rxjs complexity
   */
  getTaskId(): number {
    const ret = Number(this.getSession()?.startData?.taskId ?? 0);
    if (!ret) {
      throw new Error('taskId not found');
    }
    return ret;
  }

  /**
   * This method is an easier way to get sessionId from session
   */
  getSessionId(): string {
    try {
      const ret = this.getSession()?.id;
      if (!ret) {
        throw new Error('sessionId not found');
      }
      return ret;
    } catch (error) {
      debug('getSessionId error', error);
      throw new Error('sessionId not found', { cause: error });
    }
  }

  getTaskInfo(taskName: string): TaskInfo | undefined {
    return this.getTasks().find((x) => x.name === taskName);
  }

  getTasks(): TaskInfo[] {
    return this.getSession().processData?.tasks || [];
  }
}
